import React, { useState, useEffect, useCallback } from 'react';

interface SelectProps {
  currentLevel: number;
  currentSong: number;
  setCurrentSong: (song: number) => void;
  currentLanguage: string;
  translate: boolean;
  examples: Example[];
  top10Songs: Song[];
  initialIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
}

interface Example {
  id: string;
  level: number;
  songId: string;
  imageFull: string;
  imageUrl: string;
  index: number;
  twisted: string[];
  text: string;
  imagePermanentLink: string;
  translation: Record<string, string>;
}

interface Song {
  id: string;
  name: string;
  artist: string;
  position: number;
  yt: string[];
  ytIndex: number;
  translation: Record<string, string>;
}

const Select: React.FC<SelectProps> = ({
  currentLevel,
  currentSong,
  setCurrentSong,
  currentLanguage,
  translate,
  examples,
  top10Songs,
  initialIndex,
  onNext,
  onPrevious
}) => {
  const [shuffledExamples, setShuffledExamples] = useState<Example[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [options, setOptions] = useState<string[]>([]);
  const [celebrating, setCelebrating] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Shuffle array function
  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Initialize shuffled examples
  useEffect(() => {
    if (examples && examples.length > 0) {
      setShuffledExamples(shuffleArray(examples));
      setCurrentIndex(initialIndex ?? 0);
    }
  }, [examples, initialIndex]);

  useEffect(() => {
    if (shuffledExamples.length > 0) {
      setCurrentIndex(initialIndex ?? 0);
    }
  }, [initialIndex, shuffledExamples.length]);

  // Setup options when current example changes
  useEffect(() => {
    if (shuffledExamples.length > 0) {
      const currentExample = shuffledExamples[currentIndex];
      const allOptions = [
        currentExample.text,
        ...currentExample.twisted
      ];
      setOptions(shuffleArray(allOptions));
      setSelectedOption(null);
    }
  }, [currentIndex, shuffledExamples]);

  // Text-to-speech function
  const speakText = useCallback((text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  // Handle option selection
  const handleOptionClick = (option: string) => {
    if (celebrating) return;

    const currentExample = shuffledExamples[currentIndex];
    setSelectedOption(option);

    if (option === currentExample.text) {
      // Correct answer
      setCelebrating(true);
      speakText(currentExample.text);

      setTimeout(() => {
        setCelebrating(false);
        const nextIndex = (currentIndex + 1) % shuffledExamples.length;

        // Reshuffle if we completed all examples
        if (nextIndex === 0) {
          setShuffledExamples(shuffleArray(examples));
          if (onNext) {
            onNext();
            return;
          }
        }

        setCurrentIndex(nextIndex);
      }, 2000);
    } else {
      // Wrong answer - shake and reset
      setTimeout(() => {
        setSelectedOption(null);
      }, 500);
    }
  };

  const goToNext = () => {
    if (onNext) {
      onNext();
      return;
    }

    if (currentIndex < shuffledExamples.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goToPrevious = () => {
    if (onPrevious) {
      onPrevious();
      return;
    }

    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Touch handlers for swipe navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'Enter' || e.key === ' ') {
        // If focus is not on an option button, treat Enter/Space as Next
        const active = document.activeElement as HTMLElement | null;
        const isOptionFocused = active && active.classList && active.classList.contains('option-button');
        if (!isOptionFocused) {
          e.preventDefault();
          goToNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, shuffledExamples.length, onNext, onPrevious]);

  if (shuffledExamples.length === 0 || !shuffledExamples[currentIndex]) {
    return null;
  }

  const currentExample = shuffledExamples[currentIndex];
  const translation = translate && currentLanguage
    ? currentExample.translation[currentLanguage]
    : null;

  return (
    <div
      className="select-page-container"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '1rem',
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      {/* Image */}
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: '300px',
        aspectRatio: '4/3',
        marginBottom: '1rem',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        <img
          src={currentExample.imageUrl}
          alt={currentExample.text}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />

        {celebrating && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(34, 197, 94, 0.9)',
            animation: 'celebrationPulse 0.5s ease-in-out',
            zIndex: 10
          }}>
            <div style={{
              fontSize: '4rem',
              animation: 'celebrationBounce 0.6s ease-in-out'
            }}>
              🎉
            </div>
          </div>
        )}
      </div>

      {/* Play button */}
      <button
        onClick={() => speakText(currentExample.text)}
        disabled={celebrating}
        style={{
          padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 3vw, 24px)',
          fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: celebrating ? 'not-allowed' : 'pointer',
          marginBottom: '1rem',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          transition: 'transform 0.2s',
          fontWeight: '600',
          whiteSpace: 'nowrap'
        }}
        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
        onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        🔊 Play
      </button>

      {/* Translation */}
      {translation && (
        <div style={{
          fontSize: 'clamp(1.75rem, 5.5vw, 3.5rem)',
          color: '#0b3d91',
          marginBottom: '1.5rem',
          textAlign: 'center',
          textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          fontWeight: '500',
          lineHeight: 1.2
        }}>
          {translation}
        </div>
      )}

      {/* Options */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        width: '100%',
        maxWidth: '600px'
      }}>
        {options.map((option, index) => {
          const isCorrect = option === currentExample.text;
          const isSelected = selectedOption === option;
          const isWrong = isSelected && !isCorrect;

          return (
            <button
              key={index}
              className="option-button"
              onClick={() => handleOptionClick(option)}
              disabled={celebrating}
              style={{
                padding: 'clamp(0.75rem, 2vh, 1.5rem) clamp(1rem, 3vw, 2rem)',
                fontSize: 'clamp(1.25rem, 4vw, 2rem)',
                fontWeight: '600',
                borderRadius: '12px',
                border: isSelected
                  ? isCorrect
                    ? '3px solid rgb(34, 197, 94)'
                    : '3px solid rgb(239, 68, 68)'
                  : '2px solid rgba(255, 255, 255, 0.3)',
                background: isSelected
                  ? isCorrect
                    ? 'rgba(34, 197, 94, 0.8)'
                    : 'rgba(239, 68, 68, 0.8)'
                  : 'rgba(255, 255, 255, 0.15)',
                color: '#0b3d91',
                cursor: celebrating ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                animation: isWrong ? 'shake 0.5s' : 'none',
                boxShadow: isSelected
                  ? isCorrect
                    ? '0 4px 16px rgba(34, 197, 94, 0.4)'
                    : '0 4px 16px rgba(239, 68, 68, 0.4)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                if (!celebrating && !isSelected) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!celebrating && !isSelected) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {option}
            </button>
          );
        })}
      </div>

      {/* Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        maxWidth: '600px',
        marginTop: '1.5rem'
      }}>
        <button
          onClick={goToPrevious}
          style={{
            padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 2vw, 20px)',
            fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
            backgroundColor: 'rgba(100,100,100,0.8)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            backdropFilter: 'blur(10px)',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}
        >
          ← Previous
        </button>

        <span style={{
          color: 'white',
          fontSize: '1rem',
          textShadow: '0 2px 4px rgba(0,0,0,0.5)',
          backgroundColor: 'rgba(0,0,0,0.3)',
          padding: '8px clamp(8px, 2vw, 16px)',
          borderRadius: '8px',
          backdropFilter: 'blur(10px)',
          whiteSpace: 'nowrap'
        }}>
          {currentIndex + 1} / {shuffledExamples.length}
        </span>

        <button
          onClick={goToNext}
          style={{
            padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 2vw, 20px)',
            fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
            backgroundColor: 'rgba(100,100,100,0.8)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            backdropFilter: 'blur(10px)',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}
        >
          Next →
        </button>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }

        @keyframes celebrationPulse {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }

        @keyframes celebrationBounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.3); }
        }

        @media (max-width: 640px) {
          .select-page-container button {
            font-size: 0.95rem !important;
            padding: 0.875rem 1rem !important;
          }
          .select-page-container .option-button {
            font-size: 1.35rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Select;
